import { create } from 'zustand';
import { apiClient } from '../api/client';
import { useAuthStore } from './authStore';
import { queryClient } from '../queryClient';
import { invalidateUserActivity } from '../queries/mutations';
import { session } from '../../platform';

const ACTIVE_GENERATIONS_STORAGE_KEY = 'activeGenerations';
const ACTIVE_GENERATION_TTL_MS = 60 * 60 * 1000;
const FAILED_ACTIVE_GENERATION_TTL_MS = 15 * 1000;

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.error?.message === 'string') return data.error.message;
  return error?.message || 'Generation failed';
}

function isActiveGeneration(generation) {
  return generation?.status === 'PENDING' || generation?.status === 'PROCESSING';
}

function normalizeActiveGeneration(generation) {
  if (!generation || typeof generation !== 'object') return null;
  const id = String(generation.id || '');
  if (!id) return null;
  const status = generation.status || 'PROCESSING';
  const createdAt = generation.createdAt || new Date().toISOString();
  const createdTime = new Date(createdAt).getTime();
  const now = Date.now();
  if (
    isActiveGeneration({ status }) &&
    Number.isFinite(createdTime) &&
    now - createdTime > ACTIVE_GENERATION_TTL_MS
  ) {
    return null;
  }
  if (
    status === 'FAILED' &&
    Number.isFinite(createdTime) &&
    now - createdTime > FAILED_ACTIVE_GENERATION_TTL_MS
  ) {
    return null;
  }

  return {
    ...generation,
    id,
    count: Math.max(1, Number(generation.count) || 1),
    type: generation.type || 'photo',
    status,
    createdAt,
    serverIds: Array.isArray(generation.serverIds)
      ? generation.serverIds.filter(Boolean).map(String)
      : [],
  };
}

function readPersistedActiveGenerations() {
  try {
    const raw = session.getItem(ACTIVE_GENERATIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeActiveGeneration).filter(Boolean);
  } catch {
    return [];
  }
}

function persistActiveGenerations(generations) {
  const normalized = (generations || []).map(normalizeActiveGeneration).filter(Boolean);
  try {
    if (normalized.length > 0) {
      session.setItem(ACTIVE_GENERATIONS_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      session.removeItem(ACTIVE_GENERATIONS_STORAGE_KEY);
    }
  } catch {
    /* session storage failures are non-fatal */
  }
  return normalized;
}

function hasRenderablePhoto(generation) {
  return Boolean(generation?.imageUrl || generation?.thumbnailUrl);
}

const activeImageRequests = new Map();

function createClientGenerationIds(localGenerationId, batchCount) {
  const count = Math.min(Math.max(1, Number(batchCount) || 1), 4);
  return Array.from({ length: count }, (_, index) => `${localGenerationId}-${index + 1}`);
}

export const useStore = create((set, get) => ({
  user: null,
  presets: [],
  generations: [],
  loading: false,
  error: null,
  currentGeneration: null,
  selectedPreset: null,
  promptDraftReferences: [],
  promptDraftUploading: false,

  setPromptDraftReferences: (referencesOrUpdater) => {
    set((state) => ({
      promptDraftReferences:
        typeof referencesOrUpdater === 'function'
          ? referencesOrUpdater(state.promptDraftReferences)
          : referencesOrUpdater,
    }));
  },

  uploadPromptDraftReferences: async (files = []) => {
    if (get().promptDraftUploading || files.length === 0) return [];

    set({ promptDraftUploading: true });
    const uploadedReferences = [];
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.upload.reference(formData);
        const reference = {
          id: Date.now() + Math.random(),
          preview: response.data.url,
          url: response.data.url,
        };
        uploadedReferences.push(reference);
        set((state) => ({
          promptDraftReferences: [...state.promptDraftReferences, reference],
        }));
      }
      return uploadedReferences;
    } finally {
      set({ promptDraftUploading: false });
    }
  },
  
  // Изменено: массив активных генераций вместо boolean
  activeGenerations: readPersistedActiveGenerations(), // [{id, count, status, type, createdAt, serverIds}]
  
  // Для совместимости
  get isGenerating() {
    return get().activeGenerations.some(isActiveGeneration);
  },
  get generatingCount() {
    return get().activeGenerations
      .filter(isActiveGeneration)
      .reduce((sum, g) => sum + g.count, 0);
  },

  // Старые методы для совместимости
  startGenerating: (count = 1) => {
    const id = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    set(state => {
      const activeGenerations = persistActiveGenerations([...state.activeGenerations, {
        id,
        count,
        status: 'PROCESSING',
        type: 'photo',
        createdAt: new Date().toISOString(),
      }]);
      return { activeGenerations };
    });
    return id;
  },
  
  stopGenerating: (generationId) => {
    if (generationId) {
      set(state => {
        const activeGenerations = persistActiveGenerations(
          state.activeGenerations.filter(g => g.id !== generationId)
        );
        return { activeGenerations };
      });
    } else {
      // Fallback: убираем первый
      set(state => {
        const activeGenerations = persistActiveGenerations(state.activeGenerations.slice(1));
        return { activeGenerations };
      });
    }
  },

  reconcileActiveGenerations: (galleryItems = []) => {
    const galleryIds = new Set(
      (galleryItems || [])
        .map((item) => item?.id)
        .filter(Boolean)
        .map(String)
    );

    set((state) => {
      const activeGenerations = persistActiveGenerations(
        state.activeGenerations.filter((generation) => {
          const serverIds = Array.isArray(generation.serverIds)
            ? generation.serverIds.map(String)
            : [];
          if (serverIds.length === 0) return true;
          return !serverIds.every((id) => galleryIds.has(id));
        })
      );
      return { activeGenerations };
    });
  },

  fetchUser: async () => {
    set({ loading: true, error: null });
    try {
      const response = await apiClient.user.getMe();
      set({ user: response.data.user, loading: false });
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch user', loading: false });
    }
  },

  fetchPresets: async () => {
    try {
      const response = await apiClient.generate.getPresets();
      set({ presets: response.data.presets });
    } catch (error) {}
  },

  fetchHistory: async (params = {}) => {
    try {
      const response = await apiClient.generate.getHistory(params);
      set({ generations: response.data.generations });
    } catch (error) {}
  },

  generateImage: async (presetId, prompt, aspectRatio, resolution, batchCount = 1, references = []) => {
    const referenceUrls = references.map(r => r.url).filter(Boolean);
    const requestKey = JSON.stringify({
      presetId,
      prompt,
      aspectRatio,
      resolution,
      batchCount,
      references: referenceUrls,
    });
    const activeRequest = activeImageRequests.get(requestKey);
    if (activeRequest) return activeRequest;

    const request = (async () => {
      // Создаём уникальный ID для этой генерации
      const generationId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
      const clientGenerationIds = createClientGenerationIds(generationId, batchCount);
      
      // Добавляем в активные (не блокируем кнопку!)
      set(state => {
        const activeGenerations = persistActiveGenerations([
          ...state.activeGenerations.filter((generation) => generation?.status !== 'FAILED'),
          {
            id: generationId,
            count: clientGenerationIds.length,
            status: 'PROCESSING',
            type: 'photo',
            createdAt: new Date().toISOString(),
            serverIds: clientGenerationIds,
          },
        ]);
        return { activeGenerations, error: null };
      });

      try {
        const response = await apiClient.generate.createGeneration({
          presetId,
          prompt,
          aspectRatio,
          resolution,
          batchCount,
          references: referenceUrls,
          clientGenerationIds,
        });

        const generations = (
          Array.isArray(response.data.generations)
            ? response.data.generations
            : [response.data.generation]
        ).filter(Boolean);
        const newGeneration = generations[0];

        const serverIds = generations
          .map((generation) => generation?.id)
          .filter(Boolean)
          .map(String);
        const hasCompletedPhotos =
          generations.length > 0 && generations.every(hasRenderablePhoto);

        set((state) => {
          const activeGenerations = persistActiveGenerations(
            hasCompletedPhotos
              ? state.activeGenerations.filter((generation) => generation.id !== generationId)
              : state.activeGenerations.map((generation) => (
                  generation.id === generationId
                    ? {
                        ...generation,
                        status: 'PROCESSING',
                        serverIds,
                        count: serverIds.length || generation.count || 1,
                        updatedAt: new Date().toISOString(),
                      }
                    : generation
                ))
          );
          return {
            currentGeneration: newGeneration,
            generations: [...generations, ...state.generations],
            activeGenerations,
          };
        });
        invalidateUserActivity(queryClient);

        // Обновляем юзера (кредиты)
        try {
          const { refreshUser } = useAuthStore.getState();
          await refreshUser();
        } catch (err) {}

        return newGeneration;
      } catch (error) {
        const message = extractErrorMessage(error);
        set((state) => {
          const activeGenerations = persistActiveGenerations(
            state.activeGenerations.map((generation) => (
              generation.id === generationId
                ? {
                    ...generation,
                    status: 'FAILED',
                    errorMessage: message,
                    failedAt: new Date().toISOString(),
                  }
                : generation
            ))
          );
          return {
            error: message,
            activeGenerations,
            currentGeneration: null,
          };
        });
        throw error;
      }
    })();

    activeImageRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      activeImageRequests.delete(requestKey);
    }
  },

  selectPreset: (preset) => {
    set({ selectedPreset: preset });
  },

  clearError: () => {
    set({ error: null });
  },

  refreshUser: async () => {
    const { fetchUser } = get();
    await fetchUser();
  }
}));
